<template>
  <common-dialog
    ref="dialog"
    title="Warehouse Receipt Verification"
    :width="60"
    ok-text="Save"
    :reset-inputs="resetInputs"
    :save-cb="saveComplete"
  >
    <template v-slot:content>
      <div v-loading="loadingData" style="min-height: 300px !important;">
        <div class="error-msg msg" v-if="errorMsg !== ''">
          <el-alert :title="errorMsg" type="error" :closable="false" />
        </div>
        <div class="success-msg msg" v-if="successMsg !== ''">
          <el-alert :title="successMsg" type="success" :closable="false" />
        </div>
        <el-table
          :data="groups"
          default-expand-all
          :row-class-name="rowClassBackground"
          :header-cell-style="{ background: '#f5f7fa' }"
        >
          <el-table-column type="expand">
            <template slot-scope="props">
              <div class="f-r square-list">
                <div
                  class="square-quantity f-c-c-c"
                  v-for="pallet in props.row.pallets"
                  :key="pallet.id"
                >
                  <span>{{ pallet.quantity }}</span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Group Name" prop="name" />
          <el-table-column label="Estimated Quantity" prop="quantity" />
          <el-table-column label="Actual Quantity" prop="actualQuantity" />
          <el-table-column label="Estimated Pallet Count" prop="palletCount" />
          <el-table-column
            label="Actual Pallet Counts"
            prop="actualPalletCount"
          />
          <el-table-column width="50">
            <template slot-scope="scope">
              <svg-icon
                icon-class="confirm"
                class="svg-success-color"
                style="font-size: 22px;"
                v-if="scope.row.actualQuantity === scope.row.quantity"
              />
              <svg-icon
                v-else
                icon-class="warning"
                class="svg-warning-color"
                style="font-size: 20px;"
              />
            </template>
          </el-table-column>
        </el-table>
      </div>
    </template>
  </common-dialog>
</template>

<script>
// components
import CommonDialog from '@/components/common/CommonDialog.vue'
// model
import { UnloadingJobVerificationFormHeader } from '@/core/models/job'
// others
import cloneDeep from 'lodash/cloneDeep'
// configs
import * as T from '@/core/configs/types'
import { completeJob, detailJob } from '@/api/job'
import { queryPallets } from '@/api/pallet'
import { PalletDeleted, PalletUpcoming } from '@/core/models/pallet'
import { UnloadingJob } from '@/core/models/constant'
// msg
import PagesMessage from '@/core/message/pages'

export default {
  name: 'UnloadingJobVerificationDialog',
  props: {
    jobId: {
      type: String,
      required: true
    }
  },
  components: {
    CommonDialog
  },
  data() {
    return {
      header: cloneDeep(UnloadingJobVerificationFormHeader),
      T,
      groups: [],
      loadingData: false,
      errorMsg: '',
      successMsg: ''
    }
  },
  methods: {
    open(sendCb = null) {
      // fetch latest data
      this.$refs.dialog.open()
      this.getJobAndPallets()
    },
    async getJobAndPallets() {
      this.loadingData = true
      let errMsg = ''
      let flag = true
      const job = await detailJob(this.jobId)
      if (!job) {
        flag = false
        errMsg = 'Failed to get job detail'
      } else {
        const palletIds = job.processingPalletIds
        const palletPage = await queryPallets({ ids: palletIds, size: -1 })
        const pallets = palletPage?.records ?? []
        if (pallets.length === 0) {
          flag = false
          errMsg = 'Failed to get pallets for unloading job'
        } else {
          const groups = job.goodsGroups ?? []
          groups.sort((a, b) => a.name.localeCompare(b.name))
          this.groups.splice(0, this.groups.length)
          groups.forEach(group => {
            const groupPallets = pallets.filter(
              one => one.groupName === group.name
            )
            const arrived = groupPallets.filter(
              one => ![PalletDeleted, PalletUpcoming].includes(one.status)
            )
            this.groups.push({
              ...group,
              pallets: arrived,
              actualPalletCount: arrived.length,
              actualQuantity: arrived.reduce((acc, cur) => {
                return acc + cur.quantity
              }, 0)
            })
          })
        }
        this.loadingData = false
        if (!flag) this.$refs.dialog.error(errMsg)
      }
    },
    rowClassBackground({ row, rowIndex }) {
      if (row.quantity === row.actualQuantity) {
        return 'matched-row'
      }
      return 'danger-table-row'
    },
    resetInputs() {},
    async saveComplete(_, done, error) {
      if (!this.loadingData) {
        // double confirm when there is difference
        const diffs = this.groups.filter(
          one => one.quantity !== one.actualQuantity
        )
        if (diffs.length > 0) {
          const res = await this.$confirm(
            'Quantity in some group are not matched?',
            'Warning',
            {
              confirmButtonText: 'Continue',
              cancelButtonText: 'Cancel',
              type: 'warning'
            }
          )
          if (res !== 'confirm') return
        }
        completeJob({
          jobId: this.jobId,
          type: UnloadingJob,
          groups: this.groups
        })
          .then(res => {
            if (res) {
              this.$message.success('Successfully completed unloading job!')
              PagesMessage.emit(PagesMessage.eventMap.REFRESH_JOB_DETAIL)
              done()
            } else {
              error('Failed to save warehouse receipt')
            }
          })
          .catch(err => error(err))
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/common.scss';

::v-deep .unmatched-row {
  background: oldlace;
}

::v-deep .el-table__expanded-cell {
  padding: 10px;
}

::v-deep .cell {
  justify-content: center;
  text-align: center;

  @extend .f-r;
}

.msg {
  margin-bottom: 20px;
}

.square-list {
  margin-left: 50px;
  flex-wrap: wrap;
  gap: 5px;
}

.square-quantity {
  width: 40px;
  height: 40px;
  font-size: 27px;
  font-weight: bolder;
  text-align: center;
  border: 1px solid black;
}
</style>
